import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Paper, TextField, Grid, IconButton,
    CircularProgress, Alert, Breadcrumbs, Link, Switch, FormControlLabel,
    MenuItem, Divider, InputAdornment, FormControl, InputLabel, Select, Checkbox, ListItemText, OutlinedInput
} from '@mui/material';
import { ArrowBack, Save, LocalOffer } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createCoupon, updateCoupon, getAllServiceLocations } from '../../services/Api';

// Handles "2026-04-22 05:30:00" or "2026-04-22T05:30:00.000Z" -> "2026-04-22" for <input type="date">
const toDateInput = (dateStr) => {
    if (!dateStr) return '';
    // Already in YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    // "YYYY-MM-DD HH:mm:ss" or ISO
    return dateStr.replace(' ', 'T').split('T')[0];
};

const INITIAL_FORM = {
    code: '',
    type: 'Food',
    discountType: 'percentage',
    discountValue: 0,
    minOrderAmount: 0,
    maxValue: 0,
    startDate: '',
    endDate: '',
    usageLimit: 0,
    isActive: true,
    isFreeDelivery: false,
    isFreeService: false,
    locationIds: [],
};

const CreateAndModifyCoupons = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const coupon = location.state?.coupon;

    const [formData, setFormData] = useState(() => {
        if (isEdit && coupon) {
            let parsedLocationIds = [];
            if (coupon.locations && Array.isArray(coupon.locations)) {
                parsedLocationIds = coupon.locations.map(loc => loc.id || loc._id);
            } else if (coupon.locationIds) {
                parsedLocationIds = Array.isArray(coupon.locationIds)
                    ? coupon.locationIds
                    : coupon.locationIds.split(',').filter(Boolean);
            }

            return {
                code: coupon.code || '',
                type: coupon.type || 'Food',
                discountType: coupon.discountType || 'percentage',
                discountValue: coupon.discountValue ?? 0,
                minOrderAmount: coupon.minOrderAmount ?? 0,
                maxValue: coupon.maxValue ?? 0,
                startDate: toDateInput(coupon.startDate),
                endDate: toDateInput(coupon.endDate),
                usageLimit: coupon.usageLimit ?? 0,
                isActive: coupon.isActive ?? true,
                isFreeDelivery: coupon.isFreeDelivery ?? false,
                isFreeService: coupon.isFreeService ?? false,
                locationIds: parsedLocationIds,
            };
        }
        return INITIAL_FORM;
    });

    const [serviceLocations, setServiceLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [codeError, setCodeError] = useState('');

    const today = new Date();
    const todayDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    useEffect(() => {
        if (isEdit && !coupon) setError('Please navigate from the coupons list to edit.');

        const fetchInitialData = async () => {
            try {
                const locRes = await getAllServiceLocations(1, 100);
                if (locRes.success) {
                    setServiceLocations(Array.isArray(locRes.data) ? locRes.data : (locRes.data?.docs || []));
                }
            } catch (err) {
                console.error("Failed to fetch locations", err);
            }
        };
        fetchInitialData();
    }, [isEdit, coupon]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'code') {
            setCodeError('');
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleLocationChange = (e) => {
        const { value } = e.target;
        setFormData(prev => ({
            ...prev,
            locationIds: typeof value === 'string' ? value.split(',') : value
        }));
    };

    const handleSwitch = (name) => (e) => {
        setFormData(prev => ({ ...prev, [name]: e.target.checked }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const codeRegex = /^[a-zA-Z0-9]+$/;
        if (!codeRegex.test(formData.code.trim())) {
            setCodeError('Coupon code must contain only alphabets and numbers');
            setLoading(false);
            return;
        }

        if (!formData.locationIds || formData.locationIds.length === 0) {
            setError('Please select at least one Service Location');
            setLoading(false);
            return;
        }

        const payload = {
            code: formData.code,
            type: formData.type,
            discountType: formData.discountType,
            discountValue: Number(formData.discountValue),
            minOrderAmount: Number(formData.minOrderAmount),
            maxValue: Number(formData.maxValue),
            startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
            endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
            usageLimit: Number(formData.usageLimit),
            isActive: formData.isActive,
            isFreeDelivery: formData.isFreeDelivery,
            isFreeService: formData.isFreeService,
            locationIds: formData.locationIds,
        };

        try {
            const response = isEdit ? await updateCoupon(id, payload) : await createCoupon(payload);
            if (response.success || response.data || response.id) {
                navigate('/coupons');
            } else {
                setError(response.message || 'Failed to save coupon');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while saving the coupon');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs sx={{ mb: 1 }}>
                    <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/coupons'); }}>
                        Coupons
                    </Link>
                    <Typography color="text.primary">{isEdit ? 'Modify' : 'Create'}</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/coupons')} sx={{ border: '1px solid', borderColor: 'text.border' }}>
                        <ArrowBack />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LocalOffer color="primary" />
                        <Typography variant="h4" sx={{ fontWeight: 300 }}>
                            {isEdit ? 'Modify Coupon' : 'Create New Coupon'}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ p: 4, borderRadius: '15px', border: '1px solid', borderColor: 'text.border' }}>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>

                        {/* Section: Basic Info */}
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Basic Information</Typography>
                            <Divider />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField size="small"
                                required fullWidth label="Coupon Code" name="code"
                                value={formData.code} onChange={handleChange}
                                placeholder="e.g., SAVE20"
                                error={Boolean(codeError)}
                                helperText={codeError || "Unique alphanumeric code"}
                                inputProps={{ style: { textTransform: 'uppercase', letterSpacing: 2 } }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField size="small"
                                required fullWidth select label="Type" name="type"
                                value={formData.type} onChange={handleChange}
                            >
                                <MenuItem value="Food">Food</MenuItem>
                                <MenuItem value="Grocery">Grocery</MenuItem>
                                <MenuItem value="Services">Services</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel id="location-select-label">Service Locations</InputLabel>
                                <Select
                                    labelId="location-select-label"
                                    name="locationIds"
                                    multiple
                                    value={formData.locationIds}
                                    onChange={handleLocationChange}
                                    input={<OutlinedInput label="Service Locations" />}
                                    // eslint-disable-next-line eqeqeq
                                    renderValue={(selected) => selected.map(id => serviceLocations.find(l => (l.id || l._id) == id)?.name || id).join(', ')}
                                >
                                    {serviceLocations.map((loc) => (
                                        <MenuItem key={loc.id || loc._id} value={loc.id || loc._id}>
                                            <Checkbox checked={formData.locationIds.indexOf(loc.id || loc._id) > -1} />
                                            <ListItemText primary={loc.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField size="small"
                                required fullWidth select label="Discount Type" name="discountType"
                                value={formData.discountType} onChange={handleChange}
                            >
                                <MenuItem value="percentage">Percentage (%)</MenuItem>
                                <MenuItem value="fixed">Fixed Amount (₹)</MenuItem>
                            </TextField>
                        </Grid>

                        {/* Section: Discount & Limits */}
                        <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Discount & Limits</Typography>
                            <Divider />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField size="small"
                                required fullWidth label="Discount Value (%)"
                                name="discountValue" type="number" value={formData.discountValue}
                                onChange={handleChange}
                                InputProps={{ inputProps: { min: 0, max: 100 }, startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                                helperText="Percentage to deduct"
                                sx={{ display: formData.discountType === 'percentage' ? 'block' : 'none' }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField size="small"
                                required fullWidth
                                label={formData.discountType === 'percentage' ? 'Max Value (₹ cap)' : 'Fixed Amount (₹)'}
                                name="maxValue" type="number" value={formData.maxValue}
                                onChange={handleChange}
                                InputProps={{
                                    inputProps: { min: 0 },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {formData.discountType === 'percentage' ? '%' : '₹'}
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField size="small"
                                required fullWidth label="Min Order Amount (₹)"
                                name="minOrderAmount" type="number" value={formData.minOrderAmount}
                                onChange={handleChange}
                                InputProps={{ inputProps: { min: 0 }, startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField size="small"
                                fullWidth label="Usage Limit" name="usageLimit"
                                type="number" value={formData.usageLimit} onChange={handleChange}
                                InputProps={{ inputProps: { min: 0 } }}
                                helperText="0 = unlimited"
                            />
                        </Grid>

                        {/* Section: Validity */}
                        <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Validity Period</Typography>
                            <Divider />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField size="small"
                                required fullWidth label="Start Date" name="startDate"
                                type="date" value={formData.startDate} onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: todayDate }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField size="small"
                                required fullWidth label="End Date" name="endDate"
                                type="date" value={formData.endDate} onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: formData.startDate || todayDate }}
                            />
                        </Grid>

                        {/* Section: Flags */}
                        <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Flags & Status</Typography>
                            <Divider />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                            <FormControlLabel
                                control={<Switch checked={formData.isActive} onChange={handleSwitch('isActive')} color="primary" />}
                                label="Active"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <FormControlLabel
                                control={<Switch checked={formData.isFreeDelivery} onChange={handleSwitch('isFreeDelivery')} color="primary" />}
                                label="Free Delivery"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <FormControlLabel
                                control={<Switch checked={formData.isFreeService} onChange={handleSwitch('isFreeService')} color="primary" />}
                                label="Free Service"
                            />
                        </Grid>

                        {/* Actions */}
                        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                            <Button variant="outlined" sx={{ borderRadius: '8px', px: 4 }} onClick={() => navigate('/coupons')} disabled={loading}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained"
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                sx={{ borderRadius: '8px', px: 4 }} disabled={loading}>
                                {isEdit ? 'Update Coupon' : 'Save Coupon'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default CreateAndModifyCoupons;
